import { useState } from 'react';
import type { Component, ReviewItem } from '@reelmachine/core';
import { useIsStaff, useLabelStep, useReviewQueue } from '../../api/learning';
import { formatObjective, formatPercent } from '../../lib/learning-format';
import { useToast } from '../../ui/Toast';
import { QueryState } from './QueryState';

type Verdict = 'ok' | 'error' | 'upstream';
const VERDICTS: Array<{ value: Verdict; label: string }> = [
  { value: 'ok', label: 'ok' },
  { value: 'error', label: 'error' },
  { value: 'upstream', label: 'upstream' },
];

const FIELD: Record<string, string> = { text: 'spoken', onScreen: 'on screen', visual: 'visual', line: 'whole line' };

function StepRow({
  reelId,
  step,
  suspected,
  saved,
  canLabel,
  onSaved,
}: {
  reelId: string;
  step: ReviewItem['steps'][number];
  suspected: boolean;
  saved: Verdict | undefined;
  canLabel: boolean;
  onSaved: (v: Verdict) => void;
}) {
  const toast = useToast();
  const label = useLabelStep();
  const [note, setNote] = useState('');
  const id = `${reelId}-${step.component}`;
  const save = (verdict: Verdict) =>
    label.mutate(
      { reelId, component: step.component, verdict, note: note.trim() },
      { onSuccess: () => onSaved(verdict), onError: (e) => toast(e.message) },
    );
  return (
    <li className="review-step" aria-label={step.component}>
      <div className="row between nowrap">
        <strong>{step.component}</strong>
        {suspected && <span className="chip warn">Suspected</span>}
      </div>
      <p className="small">{step.summary}</p>
      {canLabel && (
        <>
          <div className="row nowrap" role="group" aria-label={`Verdict for ${step.component}`}>
            {VERDICTS.map((v) => (
              <button
                key={v.value}
                type="button"
                className={`iconbtn verdict verdict-${v.value}`}
                aria-pressed={saved === v.value}
                disabled={label.isPending}
                onClick={() => save(v.value)}
              >
                {v.label}
              </button>
            ))}
          </div>
          <label className="visually-hidden" htmlFor={`note-${id}`}>
            Note for {step.component}
          </label>
          <input
            id={`note-${id}`}
            type="text"
            placeholder="Note (optional)"
            maxLength={1000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </>
      )}
    </li>
  );
}

function ReviewCard({
  item,
  verdicts,
  canLabel,
  onSaved,
}: {
  item: ReviewItem;
  verdicts: Partial<Record<Component, Verdict>>;
  canLabel: boolean;
  onSaved: (c: Component, v: Verdict) => void;
}) {
  const edits = item.diff?.edits ?? [];
  return (
    <article className="card" aria-label={item.title}>
      <h3>{item.title}</h3>
      <p className="nums">{[item.orgHandle, item.postedAt?.slice(0, 10)].filter(Boolean).join(' · ')}</p>
      <p>
        <span className="label">Why it is here: </span>
        {item.why}
      </p>
      <p className="small muted">
        Reach {formatObjective(item.objective)} · editors changed {formatPercent(item.editRatio)}
        {item.diff ? ` (${item.diff.charsChanged} characters)` : ''}
      </p>
      {edits.length > 0 && (
        <details>
          <summary className="label">What editors changed ({edits.length})</summary>
          <ul className="diff-list">
            {edits.map((e, i) => (
              <li key={i}>
                <span className="field-l">
                  {e.beat}, {FIELD[e.field] ?? e.field}
                </span>
                {e.before && <del>{e.before}</del>}
                {e.after && <ins>{e.after}</ins>}
              </li>
            ))}
          </ul>
        </details>
      )}
      {item.suspected.length > 0 && (
        <div className="row" style={{ gap: 6 }} aria-label="Suspected components">
          {item.suspected.map((c) => (
            <span key={c} className="chip warn">
              {c}
            </span>
          ))}
        </div>
      )}
      <ol className="plain-list">
        {item.steps.map((s) => (
          <StepRow
            key={s.component}
            reelId={item.reelId}
            step={s}
            suspected={item.suspected.includes(s.component)}
            saved={verdicts[s.component]}
            canLabel={canLabel}
            onSaved={(v) => onSaved(s.component, v)}
          />
        ))}
      </ol>
    </article>
  );
}

/** Weekly human error analysis: the worst reels, one verdict per pipeline step. */
export function ReviewTab() {
  const q = useReviewQueue();
  // Every staff member can label (the whole area is staff-only); the API enforces it.
  const canLabel = useIsStaff() === true;
  const [local, setLocal] = useState<Record<string, Partial<Record<Component, Verdict>>>>({});
  return (
    <QueryState query={q}>
      {(items) => {
        const verdictsOf = (it: ReviewItem) =>
          ({
            ...Object.fromEntries(it.labels.map((l) => [l.component, l.verdict])),
            ...local[it.reelId],
          }) as Partial<Record<Component, Verdict>>;
        const done = items.filter((it) => it.steps.every((s) => verdictsOf(it)[s.component])).length;
        if (!items.length) return <p className="muted">Nothing to review this week.</p>;
        return (
          <>
            <p className="label" role="status">
              {done} of {items.length} reviewed
            </p>
            <div className="track" aria-hidden="true">
              <i style={{ width: `${Math.round((done / items.length) * 100)}%` }} />
            </div>
            {items.map((it) => (
              <ReviewCard
                key={it.reelId}
                item={it}
                verdicts={verdictsOf(it)}
                canLabel={canLabel}
                onSaved={(c, v) => setLocal((m) => ({ ...m, [it.reelId]: { ...m[it.reelId], [c]: v } }))}
              />
            ))}
          </>
        );
      }}
    </QueryState>
  );
}
